import { useEffect, useState } from 'react';
import { BarChartOutlined, KeyOutlined, RobotOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Alert, App, AutoComplete, Button, Form, Input, InputNumber, Popconfirm, Tag } from 'antd';
import { api } from '../../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel, StatCard } from '../../components';
import { timeAgo, useLoad } from '../../lib';
import { BackToSettings } from './BackToSettings';
const MODELS = [
    { value: 'claude-sonnet-5-5', label: 'claude-sonnet-5-5 · balanced (recommended)' },
    { value: 'claude-opus-5-5', label: 'claude-opus-5-5 · most capable' },
    { value: 'claude-haiku-5-5', label: 'claude-haiku-5-5 · fastest, lowest cost' }
];
export function AiSettings() {
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState(null);
    const [dirty, setDirty] = useState(false);
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/settings/ai').then((body) => body.settings));
    const settings = data;
    useEffect(() => {
        if (!settings)
            return;
        form.setFieldsValue({ apiKey: '', model: settings.model, monthlyLimit: settings.monthlyLimit });
        setDirty(false);
    }, [settings, form]);
    async function save(values, clearKey = false) {
        setSaving(true);
        try {
            const body = await api('/v1/admin/settings/ai', { method: 'PUT', body: JSON.stringify({ ...values, clearKey }) });
            message.success(clearKey ? 'Key removed. AI reading is off.' : body.settings.apiKeySet ? 'Saved. AI reading is on.' : 'Saved');
            setTest(null);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not save the settings');
        }
        finally {
            setSaving(false);
        }
    }
    async function runTest() {
        setTesting(true);
        setTest(null);
        try {
            await api('/v1/admin/settings/ai/test', { method: 'POST', body: JSON.stringify({ apiKey: form.getFieldValue('apiKey') ?? '' }) });
            setTest({ ok: true, text: 'Anthropic accepted the key.' });
        }
        catch (err) {
            setTest({ ok: false, text: err instanceof Error ? err.message : 'The key could not be checked' });
        }
        finally {
            setTesting(false);
        }
    }
    const on = Boolean(settings?.apiKeySet);
    return (<>
      <PageHeader title="AI reading" description="Claude reads bank statements and invoices for the desktop app. The key stays on this server; customers never see it." actions={<BackToSettings />}/>
      <ErrorNotice error={error}/>
      {settings?.keyUnreadable && (<Alert type="warning" showIcon title="The saved API key cannot be read on this server" description="The server's settings key was replaced or lost, so AI reading is off until the key is entered again." style={{ marginBottom: 20 }}/>)}

      <div className="stats">
        <StatCard label="AI reading" tone={on ? 'green' : 'slate'} icon={<RobotOutlined />} loading={loading && !data} value={on ? 'On' : 'Off'} foot={settings?.updatedAt ? `Changed ${timeAgo(settings.updatedAt)}` : 'Not set up yet'}/>
        <StatCard label="Model" tone="indigo" icon={<SafetyCertificateOutlined />} loading={loading && !data} value={<span style={{ fontSize: 18 }}>{settings?.model ?? '—'}</span>} foot="Used for every reading"/>
        <StatCard label="Monthly limit" tone="amber" icon={<BarChartOutlined />} loading={loading && !data} value={settings?.monthlyLimit ?? '—'} foot="Readings per account each month"/>
      </div>

      {loading && !data ? (<Panel flush title="Anthropic" description="Uses the Claude API with this server's key.">
          <FormSkeleton sections={2}/>
        </Panel>) : (<Form form={form} layout="vertical" requiredMark={false} onFinish={(values) => void save(values)} onValuesChange={() => setDirty(true)}>
        <Panel flush title="Anthropic" description="Uses the Claude API with this server's key." extra={on ? <Tag color="success" variant="filled"><span className="status-dot"/>On</Tag> : <Tag variant="filled">Off</Tag>}>
          <div className="form-section">
            <div>
              <h4>API key</h4>
              <p>Create a key in the Anthropic Console (console.anthropic.com). It is stored encrypted and never shown again.</p>
            </div>
            <div>
              <Form.Item label="Anthropic API key" name="apiKey" extra={on ? <span><KeyOutlined style={{ color: 'var(--brand)' }}/> A key ending <span className="mono">{settings?.apiKeyHint}</span> is saved. Leave blank to keep it.</span> : 'Without a key, AI reading is off for every customer.'}>
                <Input.Password autoComplete="new-password" placeholder={on ? '••••••••••••' : 'sk-ant-…'} className="mono"/>
              </Form.Item>
              {on && (<Popconfirm title="Remove the saved key?" description="AI reading stops for every customer until a key is added again." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => save(form.getFieldsValue(), true)}>
                  <Button danger type="link" style={{ paddingInline: 0 }}>Remove key</Button>
                </Popconfirm>)}
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>Model and usage</h4>
              <p>Changes apply to the next reading; no restart needed.</p>
            </div>
            <div className="form-row">
              <Form.Item label="Model" name="model" rules={[{ required: true, pattern: /^[a-z0-9][a-z0-9.-]{2,79}$/, message: 'Enter a model ID such as claude-sonnet-5-5.' }]}>
                <AutoComplete placeholder="Choose or type a model" options={MODELS} className="mono"/>
              </Form.Item>
              <Form.Item label="Readings per account each month" name="monthlyLimit" rules={[{ required: true }]}>
                <InputNumber placeholder="100" min={1} max={100000} style={{ width: '100%' }}/>
              </Form.Item>
            </div>
          </div>

          {test && (<div style={{ padding: '0 20px 20px' }}>
              <Alert type={test.ok ? 'success' : 'error'} showIcon title={test.text}/>
            </div>)}
          <div className="form-actions">
            <Button onClick={() => void runTest()} loading={testing}>Test key</Button>
            <Button type="primary" htmlType="submit" loading={saving} disabled={!dirty && Boolean(settings?.updatedAt)}>Save changes</Button>
          </div>
        </Panel>
      </Form>)}
    </>);
}
